import { z } from 'zod';
import { claimCustomerPayment } from './claim.js';
import { LineTraceMatch } from './lineItem.js';
const orderLineItem = z.object({
    id: z.number(),
    name: z.string().nullable(),
    imgUrl: z.string().nullable(),
    sku: z.string().nullable(),
    quantity: z.number(),
    unitPrice: z.number().nullable(),
    unitTax: z.number().nullable(),
});
const orderSummary = z.object({
    id: z.number(),
    nameFromPlatform: z.string().nullable(),
    orderNumber: z.string().nullable(),
    idFromPlatform: z.string(),
    createdOn: z.string(),
    platformAdminUrl: z.string(),
    wasResolvedExternally: z.boolean(),
    lineItems: z.array(orderLineItem),
});
const lineItemSummary = z.object({
    id: z.number(),
    name: z.string().nullable(),
    imgUrl: z.string().nullable(),
    sku: z.string().nullable(),
    quantity: z.number(),
    unitPrice: z.number(),
    isShopNow: z.boolean(),
});
const customerPaymentSummary = claimCustomerPayment.pick({
    id: true,
    idFromPlatform: true,
    status: true,
    amount: true,
    capturedOn: true,
    authorizationExpiresOn: true,
    currencyCode: true,
});
const claimSummary = z.object({
    id: z.number(),
    externalId: z.string(),
    originalStoreOrderId: z.number(),
    createdOn: z.string(),
    customerPayment: customerPaymentSummary.nullable(),
    refundsIssued: z.number(),
    giftCardsIssued: z.number(),
    lineItems: z.array(lineItemSummary),
});
const originRefund = z.object({
    availableAmount: z.number().nullable(),
    refundAmount: z.number(),
    canCoverRefund: z.boolean(),
});
const lineTrace = z.discriminatedUnion('matched', [
    z.object({
        lineItemId: z.number(),
        matched: z.literal(LineTraceMatch.exact),
        /** What one unit of the traced line is worth on the originating order, prorated across the exchange. */
        unitPrice: z.number(),
        unitTax: z.number(),
        suggestedAmount: z.number(),
        originatingOrderId: z.number(),
    }),
    z.object({
        lineItemId: z.number(),
        matched: z.enum([LineTraceMatch.ambiguous, LineTraceMatch.unresolved]),
        unitPrice: z.null(),
        unitTax: z.null(),
        suggestedAmount: z.null(),
        originatingOrderId: z.null(),
    }),
]);
export const claimOrderLineage = z.object({
    currencyCode: z.string(),
    originalOrder: orderSummary,
    hops: z.array(z.object({
        claim: claimSummary,
        exchangeOrder: orderSummary,
    })),
    inputClaim: claimSummary,
    lineTraces: z
        .array(lineTrace)
        .describe('One per claim line item (lineItemId). exact: the line was traced to what the customer paid on originatingOrderId, and suggestedAmount is its credit. ambiguous (more than one earlier item matched) or unresolved (nothing in the chain matched): the amounts are null. With Claim Lineage on, that line stays at the $0 the exchange order was created at and the claim timeline says it could not be updated, so the merchant should enter what the customer paid on the line in the claim, using the original order in this lineage.'),
    originRefund: originRefund
        .nullable()
        .describe('Whether the original order can take this claim’s refunds, read live from the store platform. Null when the claim has no refund or the original order was resolved outside Corso. canCoverRefund false with an availableAmount: the original order has less left to refund than refundAmount, so finalizing still refunds it but the platform may reject or cap the refund; the merchant can lower the refund or issue store credit instead. availableAmount null: the original order could not be read, so whether it covers the refund is unknown, not a no.'),
    truncated: z
        .boolean()
        .describe('The chain was too long or could not be read to the end, so originalOrder may itself be an exchange order rather than the one the customer paid on, and $0 lines are unresolved. The merchant should value those lines by hand.'),
});
//# sourceMappingURL=claimOrderLineage.js.map